import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useUpdateChapter } from "@/api/chapters";
import PropTypes from "prop-types";
import { useState, useCallback } from "react";
import { X, KeyRound, Mail, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const formSchema = z.object({
  chapterName: z.string().min(2, {
    message: "Chapter name must be at least 2 characters.",
  }),
  targetCount: z.string().optional(),
  costPerStudentINR: z.string().optional(),
  costPerStudentForeign: z.string().optional(),
});

export function EditChapterForm({ chapter, onSuccess }) {
  const [bulkText, setBulkText] = useState("");
  const [mahalluList, setMahalluList] = useState(chapter?.mahalluList || []);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      chapterName: chapter?.chapterName || "",
      targetCount: chapter?.targetCount?.toString() || "",
      costPerStudentINR:
        chapter?.targetConfig?.costPerStudentINR?.toString() || "500",
      costPerStudentForeign:
        chapter?.targetConfig?.costPerStudentForeign?.toString() || "25",
    },
  });

  const { mutate: updateChapter, isPending } = useUpdateChapter();

  const chapterName = form.watch("chapterName");
  const targetCount = form.watch("targetCount");
  const slug = chapterName
    ? chapterName
        .toLowerCase()
        .replace(/\s+/g, "")
        .replace(/[^a-z0-9]/g, "")
    : "";
  const autoEmail = slug ? `${slug}hadia@gmail.com` : "";
  const autoPassword = slug ? `${slug}${targetCount || "0"}` : "";

  const handleBulkAdd = useCallback(() => {
    const lines = bulkText
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    const unique = [...new Set([...mahalluList, ...lines])];
    setMahalluList(unique);
    setBulkText("");
  }, [bulkText, mahalluList]);

  const removeMahallu = (name) => {
    setMahalluList(mahalluList.filter((m) => m !== name));
  };

  function onSubmit(values) {
    const submitData = {
      ...values,
      targetCount: values.targetCount ? parseInt(values.targetCount, 10) : 0,
      targetConfig: {
        costPerStudentINR: values.costPerStudentINR
          ? parseInt(values.costPerStudentINR, 10)
          : 500,
        costPerStudentForeign: values.costPerStudentForeign
          ? parseInt(values.costPerStudentForeign, 10)
          : 25,
      },
      mahalluList,
    };
    delete submitData.costPerStudentINR;
    delete submitData.costPerStudentForeign;
    updateChapter(
      { id: chapter._id, ...submitData },
      {
        onSuccess: () => {
          onSuccess?.();
        },
      },
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="chapterName"
          render={({ field }) => (
            <FormItem className="space-y-1.5">
              <FormLabel className="text-sm">Chapter Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter name" {...field} className="h-9" />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="targetCount"
          render={({ field }) => (
            <FormItem className="space-y-1.5">
              <FormLabel className="text-sm">
                Target Sponsorship Count
              </FormLabel>
              <FormControl>
                <Input
                  type="number"
                  placeholder="Number of students to sponsor"
                  {...field}
                  className="h-9"
                />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />

        {/* Target Configuration */}
        <div className="grid grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="costPerStudentINR"
            render={({ field }) => (
              <FormItem className="space-y-1.5">
                <FormLabel className="text-sm">Cost/Student (INR)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="500"
                    {...field}
                    className="h-9"
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="costPerStudentForeign"
            render={({ field }) => (
              <FormItem className="space-y-1.5">
                <FormLabel className="text-sm">
                  Cost/Student (Foreign)
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="25"
                    {...field}
                    className="h-9"
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
        </div>

        {/* Credentials preview */}
        {slug && (
          <div className="rounded-xl border bg-gradient-to-br from-muted/50 to-muted p-3 space-y-2">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
              Login Credentials
            </p>
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center h-7 w-7 rounded-lg bg-primary/10 text-primary shrink-0">
                <Mail className="h-3 w-3" />
              </div>
              <p className="font-mono text-xs font-medium truncate">
                {autoEmail}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center h-7 w-7 rounded-lg bg-primary/10 text-primary shrink-0">
                <KeyRound className="h-3 w-3" />
              </div>
              <p className="font-mono text-xs font-medium">{autoPassword}</p>
            </div>
          </div>
        )}

        {/* Mahallu List */}
        <div className="space-y-1.5">
          <FormLabel className="text-sm">Mahallu List</FormLabel>
          <Textarea
            placeholder="Paste mahallu names here (one per line)"
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            rows={3}
            className="text-sm"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={handleBulkAdd}
            disabled={!bulkText.trim()}
          >
            Add Mahallus
          </Button>
          {mahalluList.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {mahalluList.map((name) => (
                <Badge
                  key={name}
                  variant="secondary"
                  className="flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs"
                >
                  {name}
                  <button
                    type="button"
                    onClick={() => removeMahallu(name)}
                    className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20 transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
          {mahalluList.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {mahalluList.length} mahallu(s) added
            </p>
          )}
        </div>

        <Button
          type="submit"
          className="w-full h-10 mt-2 font-semibold"
          disabled={isPending}
        >
          {isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            "Save Changes"
          )}
        </Button>
      </form>
    </Form>
  );
}

EditChapterForm.propTypes = {
  chapter: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    chapterName: PropTypes.string.isRequired,
    targetCount: PropTypes.number,
    mahalluList: PropTypes.arrayOf(PropTypes.string),
    chapterHead: PropTypes.shape({
      email: PropTypes.string,
    }),
  }).isRequired,
  onSuccess: PropTypes.func,
};
